import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { DashboardData } from "../../features/admin/types";
import RefreshButton from "../../components/admin/RefreshButton";

const STAT_CARDS = [
  { key: "total_users", label: "Total Users", color: "indigo", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
  { key: "total_sellers", label: "Sellers", color: "cyan", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
  { key: "total_buyers", label: "Buyers", color: "emerald", icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" },
  { key: "total_deals", label: "Deals", color: "amber", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
  { key: "total_packages", label: "Packages", color: "violet", icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" },
  { key: "total_flights", label: "Flights", color: "sky", icon: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8" },
  { key: "total_hotels", label: "Hotels", color: "rose", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
  { key: "total_webinars", label: "Webinars", color: "fuchsia", icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" },
  { key: "total_contacts", label: "Contacts", color: "teal", icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" },
  { key: "total_community_posts", label: "Community Posts", color: "orange", icon: "M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" },
  { key: "total_leads", label: "Leads", color: "lime", icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" },
  { key: "recent_signups", label: "New (7d)", color: "pink", icon: "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" },
] as const;

const COLOR_MAP: Record<string, { bg: string; text: string; border: string }> = {
  indigo: { bg: "bg-indigo-500/10", text: "text-indigo-400", border: "border-indigo-500/20" },
  cyan: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/20" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  violet: { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/20" },
  sky: { bg: "bg-sky-500/10", text: "text-sky-400", border: "border-sky-500/20" },
  rose: { bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/20" },
  fuchsia: { bg: "bg-fuchsia-500/10", text: "text-fuchsia-400", border: "border-fuchsia-500/20" },
  teal: { bg: "bg-teal-500/10", text: "text-teal-400", border: "border-teal-500/20" },
  orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20" },
  lime: { bg: "bg-lime-500/10", text: "text-lime-400", border: "border-lime-500/20" },
  pink: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/20" },
};

const CATEGORY_PAGE_SIZE = 10;

const AdminDashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [catPage, setCatPage] = useState(0);

  const fetchDashboard = useCallback(() => {
    return adminService.getDashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-gray-400">Failed to load dashboard.</p>;

  const maxTrend = Math.max(...(data.signup_trend.map((t) => t.count) || [1]));
  const maxCat = Math.max(...(data.deals_by_category.map((c) => c.count) || [1]));

  return (
    <div>
      <AdminPageHeader
        className="mb-8"
        title="Dashboard"
        subtitle="Overview of your travel marketplace"
        actions={<RefreshButton onRefresh={fetchDashboard} />}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
        {STAT_CARDS.map((card) => {
          const c = COLOR_MAP[card.color];
          const value = data.stats[card.key as keyof typeof data.stats];
          return (
            <div key={card.key} className={`${c.bg} border ${c.border} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <svg className={`w-5 h-5 ${c.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={card.icon} />
                </svg>
                <span className="text-xs text-gray-500 font-medium">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold ${c.text}`}>{value?.toLocaleString() ?? 0}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signup Trend */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Signup Trend (30 days)</h2>
          {data.signup_trend.length === 0 ? (
            <p className="text-gray-500 text-sm">No signup data available.</p>
          ) : (
            <div className="flex items-end gap-1 h-48">
              {data.signup_trend.map((t) => (
                <div key={t.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                  <div
                    className="w-full bg-indigo-500/30 hover:bg-indigo-500/50 rounded-t transition-all cursor-pointer min-h-[2px]"
                    style={{ height: `${(t.count / maxTrend) * 100}%` }}
                  />
                  <div className="absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {t.date}: {t.count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deals by Category */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Deals by Category</h2>
          {data.deals_by_category.length === 0 ? (
            <p className="text-gray-500 text-sm">No deals data available.</p>
          ) : (
            <>
              <div className="space-y-3">
                {data.deals_by_category
                  .slice(catPage * CATEGORY_PAGE_SIZE, catPage * CATEGORY_PAGE_SIZE + CATEGORY_PAGE_SIZE)
                  .map((cat) => (
                    <div key={cat.category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-300">{cat.label}</span>
                        <span className="text-gray-500">{cat.count}</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div
                          className="h-2 bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-full transition-all"
                          style={{ width: `${(cat.count / maxCat) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
              {data.deals_by_category.length > CATEGORY_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-800">
                  <span className="text-xs text-gray-500">
                    {catPage * CATEGORY_PAGE_SIZE + 1}
                    -{Math.min(catPage * CATEGORY_PAGE_SIZE + CATEGORY_PAGE_SIZE, data.deals_by_category.length)}
                    {" "}of {data.deals_by_category.length}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCatPage((p) => Math.max(0, p - 1))}
                      disabled={catPage === 0}
                      className="px-3 py-1 text-xs rounded-md bg-gray-800 text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() =>
                        setCatPage((p) =>
                          (p + 1) * CATEGORY_PAGE_SIZE < data.deals_by_category.length ? p + 1 : p
                        )
                      }
                      disabled={(catPage + 1) * CATEGORY_PAGE_SIZE >= data.deals_by_category.length}
                      className="px-3 py-1 text-xs rounded-md bg-gray-800 text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
